<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();

const audit = computed(() =>
  store.getters['aiAudits/getAuditByConversation'](props.conversationId)
);
const uiFlags = computed(() =>
  store.getters['aiAudits/getUiFlags'](props.conversationId)
);

const pollingTimer = ref(null);
const isPolling = ref(false);

const isValidated = computed(() => !!audit.value?.validated_at);

const showLowConfidence = ref(false);
const filteredMelhorias = computed(() => {
  const items = audit.value?.resultado?.pontos_melhoria ?? [];
  if (showLowConfidence.value) return items;
  return items.filter(m => (m.confidence ?? 1) >= 0.7);
});

async function validateAudit() {
  await store.dispatch('aiAudits/validate', props.conversationId);
}

function stopPolling() {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value);
    pollingTimer.value = null;
  }
  isPolling.value = false;
}

onMounted(() => {
  // Só busca se ainda não há dados em cache para essa conversa
  if (!audit.value) {
    store.dispatch('aiAudits/fetch', props.conversationId);
  }
});

onUnmounted(() => {
  stopPolling();
});

function scoreClass(score) {
  if (score >= 8) return 'text-green-600 dark:text-green-400';
  if (score >= 6) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function severityClass(sev) {
  if (sev === 'alta') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  if (sev === 'media') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
}

async function triggerAudit() {
  stopPolling();
  isPolling.value = true;

  // Limpa resultado anterior para que o polling não pare prematuramente
  store.dispatch('aiAudits/clear', props.conversationId);
  await store.dispatch('aiAudits/trigger', props.conversationId);

  let attempts = 0;
  pollingTimer.value = setInterval(async () => {
    await store.dispatch('aiAudits/fetch', props.conversationId);
    attempts += 1;
    if (audit.value || attempts >= 24) {
      stopPolling();
    }
  }, 5000);
}
</script>

<template>
  <div class="text-sm text-slate-700 dark:text-slate-200">
    <!-- Loading inicial (só quando não há dados ainda) -->
    <div v-if="uiFlags.loading && !audit" class="flex items-center gap-2 py-3 text-slate-400">
      <span class="i-lucide-loader-2 animate-spin text-base" />
      <span>Carregando auditoria…</span>
    </div>

    <!-- Erro -->
    <div v-else-if="uiFlags.error" class="py-2 text-red-500 text-xs">
      {{ uiFlags.error }}
    </div>

    <!-- Sem auditoria + polling em andamento -->
    <div v-else-if="!audit && isPolling" class="py-2">
      <div class="flex items-center gap-2 text-slate-400 text-xs">
        <span class="i-lucide-loader-2 animate-spin text-base" />
        <span>Auditando… pode levar 15–30s.</span>
      </div>
    </div>

    <!-- Sem auditoria -->
    <div v-else-if="!audit" class="py-2">
      <p class="text-slate-400 text-xs mb-3">Nenhuma auditoria disponível.</p>
      <button
        class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-woot-600 hover:bg-woot-700 text-white disabled:opacity-50"
        :disabled="uiFlags.triggering"
        @click="triggerAudit"
      >
        <span class="i-lucide-search text-sm" />
        Auditar conversa
      </button>
    </div>

    <!-- Resultado -->
    <template v-else>
      <!-- Score + botão re-auditar -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-baseline gap-1.5">
          <span
            class="text-2xl font-bold tabular-nums"
            :class="scoreClass(audit.score_geral)"
          >{{ Number(audit.score_geral).toFixed(1) }}</span>
          <span class="text-slate-400 text-xs">/10</span>
        </div>
        <div class="flex items-center gap-2 text-xs text-slate-400">
          <span>{{ audit.agente }} · {{ audit.resultado?.unidade }}</span>
          <button
            class="i-lucide-refresh-cw hover:text-woot-600 transition-colors"
            :class="{ 'animate-spin': isPolling }"
            title="Re-auditar"
            :disabled="isPolling"
            @click="triggerAudit"
          />
        </div>
      </div>

      <!-- Pontos positivos -->
      <div v-if="audit.resultado?.pontos_positivos?.length" class="mb-3">
        <p class="font-semibold text-xs text-slate-500 uppercase tracking-wide mb-1.5">
          Pontos positivos
        </p>
        <ul class="space-y-1">
          <li
            v-for="(p, i) in audit.resultado.pontos_positivos"
            :key="i"
            class="flex gap-1.5 text-xs"
          >
            <span class="i-lucide-check-circle text-green-500 shrink-0 mt-0.5" />
            {{ p }}
          </li>
        </ul>
      </div>

      <!-- Pontos de melhoria -->
      <div v-if="filteredMelhorias.length" class="mb-3">
        <div class="flex items-center justify-between mb-1.5">
          <p class="font-semibold text-xs text-slate-500 uppercase tracking-wide">
            Pontos de melhoria ({{ filteredMelhorias.length }})
          </p>
          <button
            v-if="audit.resultado?.pontos_melhoria?.length > filteredMelhorias.length || showLowConfidence"
            class="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            @click="showLowConfidence = !showLowConfidence"
          >
            {{ showLowConfidence ? 'Ocultar baixa confiança' : `+${audit.resultado.pontos_melhoria.length - filteredMelhorias.length} duvidosos` }}
          </button>
        </div>
        <div class="space-y-2">
          <details
            v-for="(m, i) in filteredMelhorias"
            :key="i"
            class="rounded border border-slate-200 dark:border-slate-700"
          >
            <summary class="flex items-start gap-2 px-2 py-1.5 cursor-pointer list-none">
              <span
                class="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 mt-0.5 uppercase"
                :class="severityClass(m.severidade)"
              >{{ m.severidade }}</span>
              <span class="text-xs leading-snug">{{ m.descricao }}</span>
            </summary>
            <div class="px-2 pb-2 space-y-1.5 text-xs border-t border-slate-100 dark:border-slate-700 pt-1.5">
              <p v-if="m.regra_violada" class="text-slate-400">
                Regra: <span class="font-mono">{{ m.regra_violada }}</span>
              </p>
              <p v-if="m.evidencia" class="text-slate-500 italic">{{ m.evidencia }}</p>
              <div v-if="m.resposta_ideal" class="bg-green-50 dark:bg-green-900/20 rounded p-2">
                <p class="text-[10px] font-semibold text-green-700 dark:text-green-400 mb-0.5">
                  Resposta ideal
                </p>
                <p class="text-slate-700 dark:text-slate-300 leading-snug">{{ m.resposta_ideal }}</p>
              </div>
            </div>
          </details>
        </div>
      </div>

      <!-- Alucinações -->
      <div v-if="audit.resultado?.alucinacoes_detectadas?.length" class="mb-3">
        <p class="font-semibold text-xs text-red-500 uppercase tracking-wide mb-1.5">
          Alucinações ({{ audit.resultado.alucinacoes_detectadas.length }})
        </p>
        <div class="space-y-2">
          <details
            v-for="(a, i) in audit.resultado.alucinacoes_detectadas"
            :key="i"
            class="rounded border border-red-200 dark:border-red-900"
          >
            <summary class="flex items-start gap-1.5 px-2 py-1.5 cursor-pointer list-none">
              <span class="i-lucide-alert-triangle text-red-500 shrink-0 mt-0.5" />
              <span class="text-xs leading-snug">{{ a.descricao }}</span>
            </summary>
            <div class="px-2 pb-2 space-y-1.5 text-xs border-t border-red-100 dark:border-red-900/50 pt-1.5">
              <p v-if="a.regra_violada" class="text-slate-400">
                Regra: <span class="font-mono">{{ a.regra_violada }}</span>
              </p>
              <div v-if="a.resposta_ideal" class="bg-green-50 dark:bg-green-900/20 rounded p-2">
                <p class="text-[10px] font-semibold text-green-700 dark:text-green-400 mb-0.5">
                  Resposta ideal
                </p>
                <p class="text-slate-700 dark:text-slate-300 leading-snug">{{ a.resposta_ideal }}</p>
              </div>
            </div>
          </details>
        </div>
      </div>

      <!-- Recomendação -->
      <div v-if="audit.resultado?.recomendacao_acao" class="text-xs bg-slate-50 dark:bg-slate-800 rounded p-2 mb-3">
        <p class="font-semibold text-slate-500 mb-0.5">Recomendação</p>
        <p class="text-slate-600 dark:text-slate-300">{{ audit.resultado.recomendacao_acao }}</p>
      </div>

      <!-- Validação — só exibe quando há pontos de melhoria -->
      <template v-if="audit.resultado?.pontos_melhoria?.length">
        <div v-if="isValidated" class="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded px-2 py-1.5">
          <span class="i-lucide-check-circle-2 text-sm shrink-0" />
          <span>Corrigida por <strong>{{ audit.validated_by }}</strong></span>
        </div>
        <button
          v-else
          class="w-full flex items-center justify-center gap-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 text-slate-500 dark:text-slate-400 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors disabled:opacity-50"
          :disabled="uiFlags.validating"
          @click="validateAudit"
        >
          <span :class="uiFlags.validating ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-check-circle'" class="text-sm" />
          {{ uiFlags.validating ? 'Salvando…' : 'Marcar como corrigida' }}
        </button>
      </template>
    </template>
  </div>
</template>
